import React, {useMemo} from 'react'
import styled from '@emotion/styled'
import {clamp} from 'ramda'

import {InputText} from '@vio/atlas/InputText'

import {
  generatePriceInputDisplayValue,
  valueToValidNumberOrEmptyString
} from './utils'

const {useState, useCallback, useEffect} = React

type PriceInputName = 'priceMin' | 'priceMax'

interface PropsType {
  value: number
  currencySymbol: string | null | undefined
  label: string
  lowerBound: number
  name: PriceInputName
  upperBound: number
  onChange: (name: string, value: number) => void
}

const CurrencyWrapper = styled.span`
  padding: 0 6px;
`

export const PriceInput = ({
  value = 0,
  label,
  name,
  onChange,
  currencySymbol,
  upperBound,
  lowerBound
}: PropsType) => {
  const [inputValue, setInputValue] = useState<number>(value)
  const [useDisplayLogic, setUseDisplayLogic] = useState<boolean>(true)
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const toggleFocus = useCallback(
    () => setIsInputFocused(!isInputFocused),
    [isInputFocused]
  )

  const handleBlur = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      toggleFocus()
      const {name, value} = e.target
      const validatedValue = clamp(lowerBound, upperBound, inputValue)

      setUseDisplayLogic(true)

      if (validatedValue !== Number(value)) {
        setInputValue(validatedValue)
      }

      onChange(name, validatedValue)
    },
    [inputValue, lowerBound, onChange, upperBound, toggleFocus]
  )

  const handleChange = useCallback(
    (value: string) => {
      const adjustedValue = valueToValidNumberOrEmptyString(value, inputValue)

      setUseDisplayLogic(false)
      setInputValue(adjustedValue ?? null)
    },
    [inputValue]
  )

  const displayValue = useMemo(() => {
    const generatedPrice = generatePriceInputDisplayValue(
      inputValue,
      name,
      upperBound
    )
    if (useDisplayLogic && generatedPrice) {
      return generatedPrice
    }

    return inputValue
  }, [inputValue, name, upperBound, useDisplayLogic])

  return (
    <InputText
      id={name}
      label={label}
      prefix={
        <CurrencyWrapper translate="no">{currencySymbol}</CurrencyWrapper>
      }
      dataId={`PriceField-${name}`}
      name={name}
      value={`${displayValue || '0'}`}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={toggleFocus}
    />
  )
}
